import React from 'react';
import { CollaborativeChartsReviewItem, PatientActivity, ProviderChartItemType } from '@uplift/api';
import { getAppointmentTitle, ModalContent, ModalTitle, ReviewLayout, ScrollableView } from '@uplift/ui-v2';
import { ProviderMedicalRole } from '@uplift/types';
import { SessionClinicalNotesReview } from '../../../components/patient-chart-review-modal/components/SessionClinicalNotesReview.component';
import { MedicalNoteReview } from './MedicalNoteReview';
import { Questionnaire } from './Questionare';
import { ChartsReviewLayout } from './ChartsReviewLayout';
import { Box, Divider, Drawer, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { matchByIdQuery, medicalNoteReviewByIdQuery, useAppointmentByIdQuery, useCollaborativeChartsReviewClinicalFlagsQuery, useSessionByAppointmentIdQuery } from '@uplift/queries';
import { ChartsReviewFlagButton } from './Flagging';
import { useTimezone } from '@uplift/contexts';
import { DateFormats } from '@uplift/constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { SidebarActivityList } from '../../../components/patient-chart-review-modal/components/SidebarActivityList';
import { ActivityDetailedView } from '../../../components/patient-chart-review-modal/components/ActivityDetailedView/ActivityDetailedView.component';
import { ActivityPreviewControlsProvider } from '../../../components';


const PatientPastSessions: React.VFC<{ patientId: string; toDate: string }> = (props) => {
  const [ opened, setOpened ] = React.useState(false);
  const [ selectedItem, setSelectedItem ] = React.useState<PatientActivity | null>(null);

  return (
    <>
      <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', ml: 2 }} onClick={() => setOpened(true)}>View past sessions</Typography>
      <Drawer open={opened} onClose={() => setOpened(false)} anchor="right">
        <ModalTitle onClose={() => setOpened(false)}>Past Sessions</ModalTitle>
        <Divider />
        <ModalContent noPadding>
          <React.Suspense fallback={null}>
            <ScrollableView width="70vw">
              <ActivityPreviewControlsProvider
                patientId={props.patientId}
                selectedItem={selectedItem}
                onSelectItem={setSelectedItem}
              >
                <ReviewLayout
                  sidebar={(
                    <SidebarActivityList
                      patientId={props.patientId}
                      selectedItem={selectedItem}
                      onSelectItem={setSelectedItem}
                      filters={{
                        toDate: props.toDate,
                      }}
                    />
                  )}
                  content={selectedItem && (
                    <ActivityDetailedView activityItem={selectedItem} />
                  )}
                />
              </ActivityPreviewControlsProvider>
            </ScrollableView>
          </React.Suspense>
        </ModalContent>
      </Drawer>
    </>
  );
};


const ReviewBlockTitleMedicalNote: React.VFC<{ medicalNoteId: string }> = ({ medicalNoteId }) => {
  const timezone = useTimezone();
  const { data: note } = useSuspenseQuery(medicalNoteReviewByIdQuery(medicalNoteId));
  const { data: match } = useSuspenseQuery(matchByIdQuery(note.matchId));

  return (
    <>
      Billable Note {CalendarDateService.date(note.submittedAt).tz(timezone).format(DateFormats['M/D/YYYY'])}
      <PatientPastSessions patientId={match.patientId} toDate={note.submittedAt} />
    </>
  );
};


const ReviewBlockTitleAppointment: React.VFC<{ appointmentId: string }> = ({ appointmentId }) => {
  const timezone = useTimezone();
  const { data: session } = useSessionByAppointmentIdQuery(appointmentId, { suspense: true });
  const { data: appointment } = useAppointmentByIdQuery(appointmentId, { suspense: true });

  if (!session.submittedAt) throw Error('SUS');

  return (
    <>
      {getAppointmentTitle(ProviderMedicalRole.psychiatrist, 60)} {CalendarDateService.date(session.submittedAt).tz(timezone).format(DateFormats['M/D/YYYY'])}
      <PatientPastSessions patientId={appointment.patientId} toDate={session.submittedAt} />
    </>
  );
};


export const ReviewBlockTitle: React.VFC<{ resourceType: ProviderChartItemType; resourceId: string }> = ({ resourceType, resourceId }) => {
  if (resourceType === ProviderChartItemType.billableNoteSubmitted) {
    return <ReviewBlockTitleMedicalNote medicalNoteId={resourceId} />;
  }
  if (resourceType === ProviderChartItemType.psychiatryAppointmentSubmitted) {
    return <ReviewBlockTitleAppointment appointmentId={resourceId} />;
  }

  return <>Unknown</>;
};


export const ReviewBlock: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  onGoNext: () => void;
  disabled?: boolean;
}> = ({ chartsReviewItem, disabled, onGoNext }) => {
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReviewItem.reviewId);
  const clinicalReviewFlag = clinicalReviewClinicalFlagByResource[chartsReviewItem.resourceId];
  const [ flagged, setFlagged ] = React.useState(!!clinicalReviewFlag);

  return (
    <ChartsReviewLayout
      title={(
        <ReviewBlockTitle
          resourceType={chartsReviewItem.resourceType}
          resourceId={chartsReviewItem.resourceId}
        />
      )}
      subtitle={(
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="textSecondary">saved {CalendarDateService.date(chartsReviewItem.updatedAt).fromNow()}</Typography>
          <ChartsReviewFlagButton flagged={flagged} setFlagged={setFlagged} />
        </Box>
      )}
      left={(
        <>
          {chartsReviewItem.resourceType === ProviderChartItemType.psychiatryAppointmentSubmitted && (
            <SessionClinicalNotesReview appointmentId={chartsReviewItem.resourceId} showPatientTitle={false} />
          )}
          {chartsReviewItem.resourceType === ProviderChartItemType.billableNoteSubmitted && (
            <MedicalNoteReview medicalNoteId={chartsReviewItem.resourceId} />
          )}
        </>
      )}
      right={(
        <Questionnaire
          chartsReviewItem={chartsReviewItem}
          disabled={disabled}
          flagged={flagged}
          setFlagged={setFlagged}
          onGoNext={onGoNext}
        />
      )}
    />
  );
};
